/**
 * External API Tokens
 *
 * Long-lived bearer tokens used by external integrations (e.g. Power BI) to
 * authenticate against the public `/api/external/v1/...` surface. Plaintext is
 * returned ONLY on create/rotate — it is never readable again afterwards.
 */
import api, { type AxiosResponse } from './client';

export interface ExternalApiToken {
  id: number;
  label: string;
  /** First N characters of the token (e.g. `helix_ext_3f...`) for display. */
  key_prefix: string;
  created_at: string;
  created_by_name: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  last_used_ip: string | null;
  is_active: boolean;
}

/**
 * The shape returned by the create + rotate endpoints — same as
 * {@link ExternalApiToken} plus the one-time plaintext `token` field.
 */
export interface ExternalApiTokenWithPlaintext extends ExternalApiToken {
  /** Plaintext token. Returned ONLY on create/rotate, never read again. */
  token: string;
}

interface ExternalApiTokenListResponse {
  count: number;
  results: ExternalApiToken[];
}

export const externalApiTokensAPI = {
  list: (): Promise<AxiosResponse<ExternalApiTokenListResponse>> =>
    api.get('/external-api-tokens/'),

  create: (
    label: string,
    expires_in_days?: number | null
  ): Promise<AxiosResponse<ExternalApiTokenWithPlaintext>> =>
    api.post('/external-api-tokens/', { label, expires_in_days }),

  rotate: (id: number): Promise<AxiosResponse<ExternalApiTokenWithPlaintext>> =>
    api.post(`/external-api-tokens/${id}/rotate/`),

  revoke: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/external-api-tokens/${id}/`),
};
